/**
 * LocationMapPicker — click-to-pin 2GIS MapGL location picker for the
 * seller-portal Location section (SCRUM-174). Mirrors
 * `features/admin/markets/MarketMapPicker.tsx`'s SDK-loading pattern
 * verbatim (CDN `<script>` append, no inline script — CSP-safe; 2GIS is the
 * ONLY map provider in GardEn, TRD §5.3): the map centers on the seller's
 * saved coordinates when set (or Bishkek otherwise), and clicking anywhere
 * on the map drops/moves a single pin there and reports the new coordinates
 * back to the form via `onPick`.
 *
 * Not extracted into a shared component: `admin/` -> other-persona imports
 * are deliberately avoided elsewhere in this codebase (see
 * `admin/product-review/api.ts`'s module docstring), and the reverse
 * (seller-portal reaching into `admin/markets/`) would be the same
 * violation in the other direction — so this is a parallel, seller-portal-
 * owned copy with this feature's own i18n namespace, same as
 * `ShopsMap.tsx` and `MarketMapPicker.tsx` already coexist as separate
 * per-feature 2GIS integrations rather than one shared abstraction.
 *
 * Scoped DOWN from the full ask ("search or drop a pin"): there is no
 * address search here — no geocoding/places-search backend contract exists
 * anywhere in this codebase to build one against (same gap
 * `MarketMapPicker.tsx` already documents). Click-to-pin satisfies "select a
 * point on a 2GIS map, get lat/long back"; the Lat/Lng inputs in
 * `LocationSection` remain fully editable as the manual-entry fallback.
 *
 * One-way data flow: this component reads `lat`/`lng` ONCE at mount (to
 * center + seed the initial pin) and never re-reads them afterwards — a map
 * click updates the fields (map -> fields), not the reverse.
 *
 * When `VITE_2GIS_API_KEY` is not baked into the build (local dev / CI /
 * vitest), the SDK has nothing to authenticate against, so this renders a
 * short explanatory placeholder instead of attempting to load the map —
 * manual Lat/Lng entry is unaffected either way.
 */
import { useEffect, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import type { LngLat, MapGLMap, MapGLMarker } from "@/types/2gis";
import {
  registerSellerPortalI18n,
  SELLER_PORTAL_NS,
} from "@/features/seller-portal/i18n";

/** 2GIS MapGL CDN entry point (allow-listed in the map CSP). */
const SDK_URL = "https://mapgl.2gis.com/api/js/v1";
/** Bishkek center — same default `ShopsMap`/`MarketMapPicker` use. */
const BISHKEK_CENTER: LngLat = [74.6, 42.87];
const CITY_ZOOM = 12;
/** Tighter zoom once a specific coordinate is known. */
const PIN_ZOOM = 15;

export interface LocationMapPickerProps {
  /** Current Lat/Lng field values (Decimal strings, possibly blank/invalid) —
   * read once at mount to seed the map's center + initial pin. */
  lat: string;
  lng: string;
  /** Fired with fresh Decimal-STRING coordinates (6dp) when the seller
   * clicks the map. The caller pushes these into the form. */
  onPick: (lat: string, lng: string) => void;
  height?: number | string;
}

function parseCoord(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/** 6dp is sub-meter precision — plenty for a hand-picked point, and matches
 * the Decimal-string-on-the-wire contract (lat/lng are forwarded verbatim,
 * never parsed to float). */
function toDecimalString(n: number): string {
  return n.toFixed(6);
}

export function LocationMapPicker({
  lat,
  lng,
  onPick,
  height = 260,
}: LocationMapPickerProps): ReactElement {
  registerSellerPortalI18n();
  const { t } = useTranslation(SELLER_PORTAL_NS);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapGLMap | null>(null);
  const markerRef = useRef<MapGLMarker | null>(null);
  // Keep the latest onPick without re-running the init effect.
  const onPickRef = useRef(onPick);
  onPickRef.current = onPick;

  const [errored, setErrored] = useState(false);

  const hasKey =
    typeof import.meta.env.VITE_2GIS_API_KEY === "string" &&
    import.meta.env.VITE_2GIS_API_KEY.length > 0;

  useEffect(() => {
    if (!hasKey) return;
    let cancelled = false;
    const apiKey = import.meta.env.VITE_2GIS_API_KEY;

    // Mount-time snapshot only — see the module docstring's "one-way data
    // flow" note for why this effect intentionally has no [lat, lng] deps.
    const initialLat = parseCoord(lat);
    const initialLng = parseCoord(lng);
    const hasInitialPoint = initialLat !== null && initialLng !== null;
    const initialCenter: LngLat = hasInitialPoint
      ? [initialLng as number, initialLat as number]
      : BISHKEK_CENTER;

    const placeMarker = (coords: LngLat): void => {
      const map = mapRef.current;
      const mapgl = window.mapgl;
      if (!map || !mapgl) return;
      markerRef.current?.destroy();
      markerRef.current = new mapgl.Marker(map, { coordinates: coords });
    };

    const initMap = (): void => {
      const mapgl = window.mapgl;
      const container = containerRef.current;
      if (!mapgl || !container || cancelled || mapRef.current) return;
      try {
        const map = new mapgl.Map(container, {
          center: initialCenter,
          zoom: hasInitialPoint ? PIN_ZOOM : CITY_ZOOM,
          ...(typeof apiKey === "string" && apiKey.length > 0 ? { key: apiKey } : {}),
        });
        mapRef.current = map;
        if (hasInitialPoint) placeMarker(initialCenter);
        map.on("click", (event) => {
          if (cancelled) return;
          const coords = event.lngLat;
          placeMarker(coords);
          onPickRef.current(toDecimalString(coords[1]), toDecimalString(coords[0]));
        });
      } catch {
        if (!cancelled) setErrored(true);
      }
    };

    const cleanup = (): void => {
      cancelled = true;
      markerRef.current?.destroy();
      markerRef.current = null;
      mapRef.current?.destroy();
      mapRef.current = null;
    };

    if (window.mapgl) {
      initMap();
      return cleanup;
    }
    const script = document.createElement("script");
    script.src = SDK_URL;
    script.async = true;
    script.onload = () => {
      if (!cancelled) initMap();
    };
    script.onerror = () => {
      if (!cancelled) setErrored(true);
    };
    document.head.appendChild(script);
    return () => {
      script.onload = null;
      script.onerror = null;
      cleanup();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasKey]);

  if (!hasKey) {
    return (
      <div
        data-testid="location-map-picker-unavailable"
        className="flex min-h-[88px] items-center justify-center rounded-[var(--g-radius)] border border-dashed border-[var(--g-hairline)] p-4 text-center text-xs text-[var(--g-secondary)]"
      >
        {t("settings.location.map.unavailable")}
      </div>
    );
  }

  if (errored) {
    return (
      <div
        data-testid="location-map-picker-error"
        role="alert"
        style={{ height }}
        className="grid w-full place-items-center rounded-[var(--g-radius)] border border-[var(--g-hairline)] bg-[var(--g-elevated)] text-center text-sm text-[var(--g-secondary)]"
      >
        {t("settings.location.map.error")}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div
        ref={containerRef}
        data-testid="location-map-picker"
        role="application"
        aria-label={t("settings.location.map.ariaLabel")}
        style={{ height }}
        className="w-full overflow-hidden rounded-[var(--g-radius)] border border-[var(--g-hairline)]"
      />
      <p className="text-xs text-[var(--g-secondary)]">{t("settings.location.map.hint")}</p>
    </div>
  );
}

export default LocationMapPicker;
